/**
 * HR data hooks. Everything here reads the real API (`src/client/api/hr/*`);
 * the mock collections are gone. Kept as thin wrappers so pages read
 * `useEmployees()` etc. without caring which endpoint backs them.
 */
import type { Employee, Goal, OnboardingTask, PerformanceReview, ReviewCycle, TimeOffBalance, TimeOffRequest } from '@/client/types/hr';
import { usePerfGoalsQuery, usePerfReviewsQuery, useReviewCyclesQuery } from '@/client/api/hr/perf';
import { useHrEmployeesQuery, useHrTasksQuery, useLeaveBalancesQuery, useLeaveRequestsQuery } from '@/client/api/hr/people';

const EMPTY_EMPLOYEES: Employee[] = [];
const EMPTY_TASKS: OnboardingTask[] = [];

export function useEmployees(): Employee[] {
  return useHrEmployeesQuery().data ?? EMPTY_EMPLOYEES;
}



const EMPTY_REQUESTS: TimeOffRequest[] = [];
const EMPTY_BALANCES: TimeOffBalance[] = [];

/** Time off is real (`/api/admin/v2/hr/leave-*`). */
export function useTimeOffRequests(): TimeOffRequest[] {
  return useLeaveRequestsQuery().data ?? EMPTY_REQUESTS;
}

export function useTimeOffBalances(year = new Date().getFullYear()): TimeOffBalance[] {
  return useLeaveBalancesQuery(year).data ?? EMPTY_BALANCES;
}

export function useOnboardingTasks(): OnboardingTask[] {
  return useHrTasksQuery().data ?? EMPTY_TASKS;
}

const EMPTY_CYCLES: ReviewCycle[] = [];
const EMPTY_REVIEWS: PerformanceReview[] = [];
const EMPTY_GOALS: Goal[] = [];

/** Performance is real (`/api/admin/v2/hr/perf`). Read-only here; writes go
 *  through the mutations in `api/hr/perf.ts`. */
export function useReviewCycles(): ReviewCycle[] {
  return useReviewCyclesQuery().data ?? EMPTY_CYCLES;
}

export function usePerformanceReviews(): PerformanceReview[] {
  return usePerfReviewsQuery().data ?? EMPTY_REVIEWS;
}

export function useGoals(): Goal[] {
  return usePerfGoalsQuery().data ?? EMPTY_GOALS;
}



// ---- Cross-cutting helpers ------------------------------------------------


